import { Warning } from "@phosphor-icons/react";
import { Banner, Button } from "@replicate/ui";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import { type FormEvent, type ReactNode, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { route } from "../urls";

export default function CreditReloadFailedBanner({
  username,
}: {
  username: string;
}): ReactNode {
  const [dismissed, setDismissed] = useState(false);
  const dismissForNow = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      if (event) {
        event.preventDefault();
      }

      const response = await fetch(
        route("api_clear_credit_reload_status", {
          username,
        }),
        {
          method: "POST",
          headers: {
            "X-CSRFToken": Cookies.get("csrftoken") ?? "",
          },
        }
      );

      setDismissed(response.ok);
      if (!response.ok) {
        Sentry.captureMessage("Failed to clear credit reload failed flag", {
          level: "error",
          extra: await response.json(),
        });

        toast.error(
          "Unable to dismiss reload failed banner. Please try again.",
          { position: "top-center" }
        );
      }
    },
    [username]
  );

  if (dismissed) {
    return null;
  }

  return (
    <div className="[&>*]:min-h-[6.5rem]">
      <Banner
        raised
        description={
          <div className="content-container flex items-center gap-2 flex-wrap">
            <div className="flex gap-2 items-top">
              <Warning
                size={24}
                className="flex-shrink-0 hidden sm:block"
                weight="duotone"
              />
              <div>
                <p className="font-bold">Reload failed</p>
                <p className="text-sm">
                  Your most recent credit reload has failed. Auto-reload has
                  been disabled on your account. Please set up auto-reload
                  again.
                </p>
              </div>
            </div>
          </div>
        }
        meta={
          <Button
            variant="clear"
            intent="danger"
            size="sm"
            onClick={() => dismissForNow()}
          >
            Dismiss
          </Button>
        }
        severity="error"
        onDismiss={dismissForNow}
      />
    </div>
  );
}
